/**
 * Compress an image file to reduce size while maintaining quality
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 2000)
 * @param {number} options.maxHeight - Maximum height (default: 2000)
 * @param {number} options.quality - JPEG/WebP quality 0-1 (default: 0.85)
 * @param {number} options.maxSizeKB - Target max file size in KB (default: 2048 = 2MB)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 2000,
    maxHeight = 2000,
    quality = 0.85,
    maxSizeKB = 2048
  } = options;

  // Skip compression for small files (under 500KB)
  if (file.size < 500 * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      URL.revokeObjectURL(img.src);

      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      // Use high-quality image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob with iterative quality reduction if needed
      const compress = (currentQuality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            // If still too large and quality can be reduced, try again
            if (blob.size > maxSizeKB * 1024 && currentQuality > 0.3) {
              compress(currentQuality - 0.1);
              return;
            }

            // Create a new File object with the compressed data
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });

            console.log(
              `Image compressed: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB (${((1 - compressedFile.size / file.size) * 100).toFixed(0)}% reduction)`
            );

            resolve(compressedFile);
          },
          'image/jpeg',
          currentQuality
        );
      };

      compress(quality);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Check if a file needs compression based on size
 * @param {File} file - The file to check
 * @param {number} thresholdKB - Size threshold in KB (default: 1024 = 1MB)
 * @returns {boolean}
 */
export function needsCompression(file, thresholdKB = 1024) {
  return file.size > thresholdKB * 1024;
}
